import React from 'react';
import {CommentOutlined, SendOutlined} from '@ant-design/icons';
import {Button, Drawer, Input, Spin, Typography} from 'antd';
import {diagramAiApi} from '@/api/diagramAiApi';
import type {DiagramAiChatTurn, ModelPatchDto} from '@/types/api';

const MAX_HISTORY_TURNS = 6;
const MAX_SELECTED = 40;

export type ChatMessage = {
	role: 'user' | 'assistant';
	text: string;
	appliedSummary?: string;
};

export type DiagramAiChatPanelProps = {
	projectId: number;
	diagramId: number;
	viewIndex: number;
	getModeler?: () => unknown;
};

function toHistory(messages: ChatMessage[]): DiagramAiChatTurn[] {
	return messages.slice(-MAX_HISTORY_TURNS).map(message => ({
		role: message.role,
		text: message.text,
		...(message.appliedSummary ? {appliedSummary: message.appliedSummary} : {}),
	}));
}

function summarizeAppliedPatch(patch: ModelPatchDto | null | undefined): string | undefined {
	const ops = patch?.patches;
	if (!ops?.length) return undefined;
	const parts = ops.slice(0, 12).map(op => {
		const name = op.node?.name || op.elementName || op.relationshipName || op.label || op.viewName || '';
		const id = op.identifier || op.node?.identifier || op.connection?.identifier || '';
		const rel = op.connection?.type || '';
		const ends =
			op.connection?.source && op.connection?.target
				? `${op.connection.source}→${op.connection.target}`
				: '';
		return [op.op, name, rel, ends, id].filter(Boolean).join(' ');
	});
	const extra = ops.length > 12 ? ` (+${ops.length - 12})` : '';
	return `${parts.join('; ')}${extra}`;
}

function readSelectedCanvasIds(getModeler?: () => unknown): string[] {
	const modeler = getModeler?.() as {
		getGraph?: () => {
			getSelectedCells?: () => Array<{id?: string} | null> | null;
		} | null;
	} | null;
	const cells = modeler?.getGraph?.()?.getSelectedCells?.() ?? [];
	const ids: string[] = [];
	for (const cell of cells) {
		const id = cell?.id;
		if (!id || ids.includes(id)) continue;
		ids.push(id);
		if (ids.length >= MAX_SELECTED) break;
	}
	return ids;
}

const DiagramAiChatPanel: React.FC<DiagramAiChatPanelProps> = ({
	projectId,
	diagramId,
	viewIndex,
	getModeler,
}) => {
	const [open, setOpen] = React.useState(false);
	const [input, setInput] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [messages, setMessages] = React.useState<ChatMessage[]>([]);

	React.useEffect(() => {
		setMessages([]);
	}, [diagramId]);

	const send = React.useCallback(async () => {
		const text = input.trim();
		if (!text || loading) return;
		const history = toHistory(messages);
		const selectedIdentifiers = readSelectedCanvasIds(getModeler);
		setInput('');
		setMessages(prev => [...prev, {role: 'user', text}]);
		setLoading(true);
		const res = await diagramAiApi.chat(projectId, diagramId, {
			message: text,
			viewIndex,
			...(history.length ? {history} : {}),
			...(selectedIdentifiers.length ? {selectedIdentifiers} : {}),
		});
		setLoading(false);
		if (res.error) {
			setMessages(prev => [
				...prev,
				{
					role: 'assistant',
					text: res.error?.message || `Ошибка (${res.status})`,
				},
			]);
			return;
		}
		const reply = res.data?.assistantMessage?.trim() || 'Готово.';
		const appliedSummary = summarizeAppliedPatch(res.data?.appliedPatch);
		setMessages(prev => [
			...prev,
			{
				role: 'assistant',
				text: reply,
				...(appliedSummary ? {appliedSummary} : {}),
			},
		]);
	}, [diagramId, getModeler, input, loading, messages, projectId, viewIndex]);

	return (
		<>
			<Button
				type="text"
				icon={<CommentOutlined />}
				onClick={() => setOpen(true)}
				title="AI assistant"
				aria-label="AI assistant"
				style={{flexShrink: 0}}
			/>
			<Drawer
				title="AI assistant"
				placement="right"
				size={400}
				open={open}
				onClose={() => setOpen(false)}
				styles={{body: {display: 'flex', flexDirection: 'column', padding: 16, gap: 12}}}
			>
				<Typography.Paragraph type="secondary" style={{marginBottom: 0, fontSize: 12}}>
					В каждый запрос уходит один снимок текущего вида и последние реплики этого чата.
					Выделенные фигуры учитываются. История живёт, пока открыта эта доска. Изменения
					применяются на сервере и синхронизируются по WebSocket.
				</Typography.Paragraph>
				<div
					style={{
						flex: 1,
						minHeight: 200,
						overflow: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
					}}
				>
					{messages.map((m, i) => (
						<div
							key={i}
							style={{
								alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
								maxWidth: '90%',
								padding: '8px 12px',
								borderRadius: 8,
								background: m.role === 'user' ? '#e6f4ff' : '#f5f5f5',
							}}
						>
							<Typography.Text style={{whiteSpace: 'pre-wrap'}}>
								{m.text}
							</Typography.Text>
						</div>
					))}
					{loading ? (
						<div style={{alignSelf: 'center', padding: 16}}>
							<Spin />
						</div>
					) : null}
				</div>
				<div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
					<Input.TextArea
						value={input}
						onChange={e => setInput(e.target.value)}
						placeholder="Опишите изменения на доске…"
						autoSize={{minRows: 2, maxRows: 6}}
						onPressEnter={e => {
							if (!e.shiftKey) {
								e.preventDefault();
								void send();
							}
						}}
					/>
					<Button
						type="primary"
						icon={<SendOutlined />}
						onClick={() => void send()}
						disabled={loading}
						style={{alignSelf: 'flex-end'}}
					>
						Отправить
					</Button>
				</div>
			</Drawer>
		</>
	);
};

export default DiagramAiChatPanel;
